import React, { useCallback, useEffect, useState } from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import { fetchServerStatsHistory } from "src/actions/mine_craft/serversActions";
import { useAppDispatch } from "src/store/hooks";

import Card from "src/componets/Card";
import { Segmented } from 'antd';
import AreaChart, { BLUE, PURPLE, bytesLabel, deltas, percentLabel } from "./_AreaChart";

// Samples land per minute, so refreshing the charts faster than this
// only replays the same series.
const HISTORY_POLL_MS = 60_000;

type IntervalKey = 'minute' | 'hour';
const INTERVALS: { value: IntervalKey, label: string }[] = [
  { value: 'minute', label: '6h' },
  { value: 'hour',   label: '7d' }
];

interface Sample {
  ts: string;
  cpuPercent: number;
  memoryBytes: number;
  diskBytes: number;
  networkRxBytes: number;
  networkTxBytes: number;
}

// Pterodactyl-style usage graphs fed by /stats/history. Self-loading:
// re-reads on the interval toggle, and once a minute while the Server
// tab's Live switch is on — new samples only land that often anyway.
const _UsageCharts = ({ server, live }: { server: ServerInterface | undefined, live: boolean }) => {
  const dispatch = useAppDispatch();

  const [range, setRange] = useState<IntervalKey>('minute');
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(false);

  const serverId = server?.id;

  // Failures stay silent: the charts are decoration around the live
  // stats, and the Live poll would toast every minute otherwise.
  const load = useCallback(async (silent = false) => {
    if (!serverId) return;

    if (!silent) setLoading(true);
    const resultAction = await dispatch(fetchServerStatsHistory({ id: serverId, interval: range }));
    if (!silent) setLoading(false);

    if (fetchServerStatsHistory.fulfilled.match(resultAction)) {
      setSamples(resultAction.payload?.samples || []);
    }
  }, [serverId, range, dispatch]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  useEffect(() => {
    if (!live) return;

    const timer = setInterval(() => {
      load(true).catch(console.error);
    }, HISTORY_POLL_MS);

    return () => clearInterval(timer);
  }, [live, load]);

  const cpu    = samples.map(sample => sample.cpuPercent || 0);
  const memory = samples.map(sample => sample.memoryBytes || 0);
  const rx     = deltas(samples.map(sample => sample.networkRxBytes || 0));
  const tx     = deltas(samples.map(sample => sample.networkTxBytes || 0));

  const Legend = () => (
    <div className='o-legend'>
      <span className='o-key'><i style={{ background: BLUE }} />Network In</span>
      <span className='o-key'><i style={{ background: PURPLE }} />Network Out</span>
    </div>
  );

  return (
    <Card className='v-mb-15'>
      <div className='v-srv-charts'>
      <div className='o-bar'>
        <h6 className='v-content-title'>Usage</h6>
        <Segmented
          size='small'
          value={range}
          onChange={value => setRange(value as IntervalKey)}
          options={INTERVALS}
        />
      </div>

      <div className='o-row'>
        {loading && !samples.length &&
          [0, 1, 2].map(index => <div key={index} className='o-card o-skeleton' />)
        }

        {(!loading || !!samples.length) && (
          <>
            <div className='o-card'>
              <div className='o-head'><h6>CPU Load</h6></div>
              {samples.length
                ? <AreaChart id='cpu' format={percentLabel} series={[{ color: BLUE, values: cpu }]} />
                : <div className='o-none'>No samples yet</div>}
            </div>

            <div className='o-card'>
              <div className='o-head'><h6>Memory Usage</h6></div>
              {samples.length
                ? <AreaChart id='mem' format={bytesLabel} series={[{ color: PURPLE, values: memory }]} />
                : <div className='o-none'>No samples yet</div>}
            </div>

            <div className='o-card'>
              <div className='o-head'>
                <h6>Network Traffic</h6>
                <Legend />
              </div>
              {samples.length
                ? <AreaChart
                    id='net'
                    format={bytesLabel}
                    series={[{ color: BLUE, values: rx }, { color: PURPLE, values: tx }]}
                  />
                : <div className='o-none'>No samples yet</div>}
            </div>
          </>
        )}
      </div>
    </div>
    </Card>
  );
};

export default _UsageCharts;
